import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Building2, Contact2, FolderKanban, ReceiptText, Save, Pencil, Archive,
  Plus, CheckCircle2, Circle, Trash2, Clock, Link2, Loader2, Mail, FileText,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  company: { label: "Company", Icon: Building2,
    fields: [["name", "Name", "text"], ["type", "Type", "select", ["Government Agency", "Prime", "Commercial Client", "Vendor"]],
      ["phone", "Phone", "text"], ["website", "Website", "text"], ["naics", "NAICS", "text"],
      ["address", "Address", "textarea"], ["notes", "Notes", "textarea"]] },
  contact: { label: "Contact", Icon: Contact2,
    fields: [["name", "Name", "text"], ["title", "Title", "text"],
      ["role", "Role", "select", ["Contracting Officer", "POC", "Vendor Rep", "Other"]],
      ["phone", "Phone", "text"], ["email", "Email", "text"], ["company_id", "Company", "company"],
      ["notes", "Notes", "textarea"]] },
  project: { label: "Project", Icon: FolderKanban,
    fields: [["name", "Name", "text"], ["status", "Status", "select", ["Active", "On Hold", "Complete"]],
      ["amount", "Amount", "text"], ["est_code", "EST code", "ro"], ["solicitation_number", "Solicitation", "ro"],
      ["state", "State", "text"], ["due_date", "Due date", "text"], ["notes", "Notes", "textarea"]] },
  po: { label: "Purchase Order", Icon: ReceiptText,
    fields: [["wd_code", "WD code", "ro"], ["vendor", "Vendor", "text"], ["amount", "Amount", "number"],
      ["terms", "Terms", "text"], ["status", "Status", "select", ["Open", "Issued", "Closed"]],
      ["project_name", "Project name", "text"]] },
};

const ASSOC_ROUTE = { companies: "company", contacts: "contact", deals: "deal", projects: "project", pos: "po" };
const assocLabel = { companies: "Companies", contacts: "Contacts", deals: "Deals", projects: "Projects", pos: "Purchase orders" };

export default function RecordView() {
  const { otype, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const meta = TYPE_META[otype];

  const load = useCallback(() => {
    api.get(`/records/${otype}/${id}/full`).then((r) => { setData(r.data); setForm(r.data.record); })
      .catch(() => { toast.error("Record not found"); navigate(-1); });
  }, [otype, id, navigate]);

  useEffect(() => {
    load();
    if (otype === "contact") api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [load, otype]);

  if (!meta) return <div className="p-8 text-sm text-zinc-500">Unknown record type.</div>;
  if (!data) return <div className="p-8 text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const rec = data.record;
  const Icon = meta.Icon;

  const save = async () => {
    const fields = {};
    meta.fields.forEach(([k, , type]) => { if (type !== "ro") fields[k] = form[k]; });
    try {
      const r = await api.put(`/records/${otype}/${id}`, { fields });
      setData({ ...data, record: r.data }); setForm(r.data); setEdit(false); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this record? It stays recoverable but leaves the active lists.")) return;
    await api.delete(`/records/${otype}/${id}`); toast.success("Archived"); navigate(-1);
  };

  const openAssoc = (group, r) => {
    if (group === "deals") navigate(`/deals/${r.id}`);
    else navigate(`/r/${ASSOC_ROUTE[group]}/${r.id}`);
  };

  const TABS = [["overview", "Overview"], ["associations", "Associations"], ["activity", "Activity"],
    ["notes", "Notes & Tasks"], ["files", "Files"], ["emails", "Emails"]];
  if (otype === "po") TABS.splice(5, 0, ["co", "Change orders"]);

  return (
    <div className="p-5 sm:p-8 max-w-[1600px] mx-auto" data-testid="record-view">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-3"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 rounded-lg bg-zinc-900 text-white flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">{meta.label}</div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-zinc-900 truncate" data-testid="record-title">{data.label || rec.name || rec.wd_code}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!edit ? <button data-testid="record-edit-btn" onClick={() => setEdit(true)} className="flex items-center gap-1.5 text-xs border border-zinc-300 px-3 py-1.5 rounded-md hover:bg-zinc-50"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            : <button data-testid="record-save-btn" onClick={save} className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800"><Save className="h-3.5 w-3.5" /> Save</button>}
          <button data-testid="record-archive-btn" onClick={archive} className="flex items-center gap-1.5 text-xs border border-zinc-300 text-zinc-600 px-3 py-1.5 rounded-md hover:bg-red-50 hover:text-red-600"><Archive className="h-3.5 w-3.5" /> Archive</button>
        </div>
      </div>

      <div className="flex border-b border-zinc-200 mb-5 overflow-x-auto" data-testid="record-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`rtab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${tab === k ? "border-gold-dark text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="bg-white border border-zinc-200 shadow-sm rounded-lg p-5 grid sm:grid-cols-2 gap-4" data-testid="record-overview">
          {meta.fields.map(([k, label, type, opts]) => (
            <div key={k} className={type === "textarea" ? "sm:col-span-2" : ""}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{label}</div>
              {!edit || type === "ro" ? (
                <div className="text-sm text-zinc-900 min-h-[20px]" data-testid={`field-${k}`}>
                  {type === "company" ? (companies.find((c) => c.id === rec[k])?.name || data.associations.companies[0]?.name || "—")
                    : (rec[k] || "—")}
                </div>
              ) : type === "textarea" ? (
                <textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={3} data-testid={`edit-${k}`} className="w-full border border-zinc-300 rounded px-2.5 py-1.5 text-sm outline-none focus:border-gold-dark" />
              ) : type === "select" ? (
                <select value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k}`} className="w-full border border-zinc-300 rounded px-2.5 py-1.5 text-sm outline-none focus:border-gold-dark">
                  <option value="">—</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : type === "company" ? (
                <select value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k}`} className="w-full border border-zinc-300 rounded px-2.5 py-1.5 text-sm outline-none focus:border-gold-dark">
                  <option value="">—</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              ) : (
                <input type={type === "number" ? "number" : "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k}`} className="w-full border border-zinc-300 rounded px-2.5 py-1.5 text-sm outline-none focus:border-gold-dark" />
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "associations" && (
        <div className="space-y-5" data-testid="record-associations">
          {Object.entries(data.associations).filter(([, list]) => list.length > 0).length === 0 && (
            <div className="text-sm text-zinc-400">No associated records yet.</div>
          )}
          {Object.entries(data.associations).map(([group, list]) => list.length > 0 && (
            <div key={group}>
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> {assocLabel[group]} ({list.length})</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {list.map((r) => (
                  <button key={r.id} data-testid={`assoc-${r.id}`} onClick={() => openAssoc(group, r)} className="text-left bg-white border border-zinc-200 hover:border-gold-dark shadow-sm rounded-lg px-3 py-2.5 transition-colors">
                    <div className="text-sm text-zinc-900">{r.name || r.title || r.wd_code || r.est_code}</div>
                    <div className="text-[11px] text-zinc-500">{r.role || r.type || r.vendor || r.pipeline_stage || r.status || ""}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "activity" && <ActivityList items={data.activity} />}

      {tab === "notes" && <NotesTasks otype={otype} oid={id} data={data} users={users} reload={load} />}

      {tab === "co" && <ChangeOrders po={rec} reload={load} />}

      {tab === "files" && <Files otype={otype} oid={id} files={data.files || []} reload={load} />}

      {tab === "emails" && <EmailLog emails={data.emails || []} />}
    </div>
  );
}

const EMAIL_STATUS = {
  sent: "bg-zinc-100 text-zinc-600", delivered: "bg-blue-100 text-blue-600",
  opened: "bg-emerald-100 text-emerald-700", clicked: "bg-emerald-100 text-emerald-700",
  bounced: "bg-red-100 text-red-600", complained: "bg-red-100 text-red-600",
  delayed: "bg-amber-100 text-amber-700", error: "bg-red-100 text-red-600", queued: "bg-zinc-100 text-zinc-500",
};

function EmailLog({ emails }) {
  return (
    <div className="space-y-2" data-testid="record-emails">
      {emails.length === 0 && <div className="text-sm text-zinc-400">No emails logged for this record yet.</div>}
      {emails.slice().reverse().map((e, i) => (
        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={`email-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-zinc-900 flex items-center gap-1.5 min-w-0"><Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{e.subject}</span></span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase shrink-0 ${EMAIL_STATUS[e.status] || "bg-zinc-100 text-zinc-600"}`}>{e.status}</span>
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">{e.tracking_code} · to {e.to}{e.deal_label ? ` · ${e.deal_label}` : ""}</div>
          {(e.delivered_at || e.opened_at) && (
            <div className="text-[10px] text-zinc-400 mt-0.5">
              {e.delivered_at ? `delivered ${new Date(e.delivered_at).toLocaleString()}` : ""}
              {e.opened_at ? ` · opened ${new Date(e.opened_at).toLocaleString()}` : ""}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Files({ otype, oid, files, reload }) {
  const [busy, setBusy] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/records/${otype}/${oid}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded"); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (f) => {
    if (!window.confirm(`Remove ${f.original_filename}?`)) return;
    await api.delete(`/records/files/${f.id}`); toast.success("File removed"); reload();
  };
  return (
    <div data-testid="record-files">
      <label className="inline-flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 cursor-pointer mb-3" data-testid="file-upload-label">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Upload file
        <input type="file" className="hidden" onChange={upload} disabled={busy} data-testid="file-upload-input" />
      </label>
      <div className="space-y-2">
        {files.length === 0 && <div className="text-sm text-zinc-400">No files attached.</div>}
        {files.map((f) => (
          <div key={f.id} className="bg-white border border-zinc-200 rounded-lg p-3 flex items-center justify-between group" data-testid={`file-${f.id}`}>
            <a href={`${API}/api/records/files/${f.id}/download`} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 hover:text-gold-dark">
              <FileText className="h-4 w-4 text-zinc-400 shrink-0" />
              <span className="text-sm text-zinc-800 truncate">{f.original_filename}</span>
              <span className="text-[10px] text-zinc-400">{(f.size / 1024).toFixed(0)} KB</span>
            </a>
            <button onClick={() => del(f)} className="text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100" data-testid={`file-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityList({ items }) {
  const navigate = useNavigate();
  const linkFor = (a) => {
    if (!a || !a.object_id) return null;
    if (a.object_type === "deal") return `/deals/${a.object_id}`;
    if (["company", "contact", "project", "po"].includes(a.object_type)) return `/r/${a.object_type}/${a.object_id}`;
    return null;
  };
  return (
    <div className="space-y-0" data-testid="record-activity">
      {(!items || items.length === 0) && <div className="text-sm text-zinc-400">No activity yet.</div>}
      {(items || []).map((a, i) => {
        const to = linkFor(a);
        return (
          <div key={i} data-testid={`record-activity-item-${i}`}
            onClick={to ? () => navigate(to) : undefined}
            className={`flex items-start gap-3 py-2.5 border-b border-zinc-100 last:border-0 ${to ? "cursor-pointer hover:bg-zinc-50 -mx-2 px-2 rounded" : ""}`}>
            <div className="h-6 w-6 rounded-full bg-zinc-100 flex items-center justify-center shrink-0 mt-0.5"><Clock className="h-3 w-3 text-zinc-400" /></div>
            <div className="min-w-0">
              <div className="text-sm text-zinc-800">{a.text}</div>
              <div className="text-[10px] text-zinc-400">{a.actor} · {new Date(a.at).toLocaleString()}{to ? " · open →" : ""}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NotesTasks({ otype, oid, data, users, reload }) {
  const [note, setNote] = useState("");
  const [task, setTask] = useState({ title: "", assignee_user_id: "", due_date: "" });
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/records/${otype}/${oid}/notes`, { body: note }); setNote(""); toast.success("Note added"); reload();
  };
  const addTask = async () => {
    if (!task.title.trim()) return;
    const u = users.find((x) => x.user_id === task.assignee_user_id);
    await api.post(`/records/${otype}/${oid}/tasks`, { ...task, assignee_name: u?.name || "" });
    setTask({ title: "", assignee_user_id: "", due_date: "" }); toast.success("Task added"); reload();
  };
  const toggle = async (t) => { await api.put(`/records/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" }); reload(); };
  const delNote = async (n) => { await api.delete(`/records/notes/${n.id}`); reload(); };
  const delTask = async (t) => { await api.delete(`/records/tasks/${t.id}`); reload(); };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="record-notes-tasks">
      <div>
        <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Notes</h3>
        <div className="flex gap-2 mb-3">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="note-input" className="flex-1 border border-zinc-300 rounded px-2.5 py-1.5 text-sm outline-none focus:border-gold-dark" />
          <button onClick={addNote} data-testid="note-add" className="bg-zinc-900 text-white text-xs px-3 rounded-md hover:bg-zinc-800"><Plus className="h-4 w-4" /></button>
        </div>
        <div className="space-y-2">
          {data.notes.length === 0 && <div className="text-xs text-zinc-400">No notes.</div>}
          {data.notes.map((n) => (
            <div key={n.id} className="bg-white border border-zinc-200 rounded-lg p-2.5 group" data-testid={`note-${n.id}`}>
              <div className="text-sm text-zinc-800">{n.body}</div>
              <div className="flex items-center justify-between mt-1"><span className="text-[10px] text-zinc-400">{n.author} · {new Date(n.created_at).toLocaleString()}</span>
                <button onClick={() => delNote(n)} className="text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button></div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Tasks</h3>
        <div className="space-y-2 mb-3 bg-zinc-50 border border-zinc-200 rounded-lg p-2.5">
          <input value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} placeholder="Task title…" data-testid="task-title" className="w-full border border-zinc-300 rounded px-2.5 py-1.5 text-sm outline-none focus:border-gold-dark" />
          <div className="flex gap-2">
            <select value={task.assignee_user_id} onChange={(e) => setTask({ ...task, assignee_user_id: e.target.value })} data-testid="task-assignee" className="flex-1 border border-zinc-300 rounded px-2 py-1.5 text-xs outline-none">
              <option value="">Assignee…</option>{users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
            <input type="date" value={task.due_date} onChange={(e) => setTask({ ...task, due_date: e.target.value })} data-testid="task-due" className="border border-zinc-300 rounded px-2 py-1.5 text-xs outline-none" />
            <button onClick={addTask} data-testid="task-add" className="bg-zinc-900 text-white text-xs px-3 rounded-md hover:bg-zinc-800"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="space-y-2">
          {data.tasks.length === 0 && <div className="text-xs text-zinc-400">No tasks.</div>}
          {data.tasks.map((t) => (
            <div key={t.id} className="bg-white border border-zinc-200 rounded-lg p-2.5 flex items-center gap-2 group" data-testid={`task-${t.id}`}>
              <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`}>{t.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-zinc-300" />}</button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${t.status === "done" ? "line-through text-zinc-400" : "text-zinc-800"}`}>{t.title}</div>
                <div className="text-[10px] text-zinc-400">{t.assignee_name || "Unassigned"}{t.due_date ? ` · due ${t.due_date}` : ""}</div>
              </div>
              <button onClick={() => delTask(t)} className="text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChangeOrders({ po, reload }) {
  const add = async () => {
    const amount = parseFloat(prompt("Change order amount ($):") || "0");
    const description = prompt("Change order description:") || "";
    if (!description) return;
    await api.post(`/pos/${po.id}/change-order`, { amount, description }); toast.success("Change order added"); reload();
  };
  return (
    <div data-testid="record-change-orders">
      <button onClick={add} data-testid="add-change-order" className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 mb-3"><Plus className="h-3.5 w-3.5" /> Add change order</button>
      <div className="space-y-2">
        {(po.change_orders || []).length === 0 && <div className="text-sm text-zinc-400">No change orders.</div>}
        {(po.change_orders || []).map((co, i) => (
          <div key={i} className="bg-white border border-zinc-200 rounded-lg p-3 flex items-center justify-between" data-testid={`co-${i}`}>
            <span className="font-mono text-sm text-zinc-900">{co.code}</span>
            <span className="text-xs text-zinc-500 flex-1 px-3 truncate">{co.description}</span>
            <span className="text-sm text-zinc-900">${Number(co.amount).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
